import React from "react";
import {
  CaseManagementEvent,
  CASE_MANAGEMENT_EVENT_TYPE_LABELS,
  CONTACT_METHOD_LABELS,
} from "@visitproof/types";
import { Badge } from "../../../../design/badge";
import { Text } from "../../../../design/text";
import {
  ChatBubbleLeftRightIcon,
  HandRaisedIcon,
  ArrowTopRightOnSquareIcon,
  BuildingLibraryIcon,
  EllipsisHorizontalIcon,
  PencilIcon,
  TrashIcon,
  BellAlertIcon,
} from "@heroicons/react/20/solid";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "../../../../design/dropdown";
import moment from "moment";

interface Props {
  event: CaseManagementEvent;
  onEdit: (event: CaseManagementEvent) => void;
  onDelete: (event: CaseManagementEvent) => void;
}

const getEventTypeIcon = (eventType: string) => {
  switch (eventType) {
    case "client_communication":
      return <ChatBubbleLeftRightIcon className="w-5 h-5" />;
    case "intervention":
      return <HandRaisedIcon className="w-5 h-5" />;
    case "referral":
      return <ArrowTopRightOnSquareIcon className="w-5 h-5" />;
    case "court_cps_update":
      return <BuildingLibraryIcon className="w-5 h-5" />;
    default:
      return <EllipsisHorizontalIcon className="w-5 h-5" />;
  }
};

const getEventTypeBadgeColor = (eventType: string): "blue" | "green" | "amber" | "purple" | "zinc" => {
  switch (eventType) {
    case "client_communication":
      return "blue";
    case "intervention":
      return "amber";
    case "referral":
      return "purple";
    case "court_cps_update":
      return "green";
    default:
      return "zinc";
  }
};

const CaseManagementEventCard: React.FC<Props> = ({ event, onEdit, onDelete }) => {
  // Safely parse occurredAt date - handle both Firestore Timestamp and ISO string
  let occurredAt: Date;
  try {
    if (event.occurredAt?.toDate) {
      // Firestore Timestamp object
      occurredAt = event.occurredAt.toDate();
    } else if (typeof event.occurredAt === 'string') {
      // ISO string from callable function return
      occurredAt = new Date(event.occurredAt);
    } else if ((event.occurredAt as any)?._seconds) {
      // Firestore Timestamp serialized format
      occurredAt = new Date((event.occurredAt as any)._seconds * 1000);
    } else {
      occurredAt = new Date(event.occurredAt as any);
    }

    // Validate the date
    if (isNaN(occurredAt.getTime())) {
      occurredAt = new Date();
    }
  } catch {
    occurredAt = new Date();
  }

  const formattedDate = moment(occurredAt).format("MMM D, YYYY");
  const formattedTime = moment(occurredAt).format("h:mm A");

  return (
    <div className="relative flex gap-4 p-4 transition-colors bg-white border rounded-lg shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600">
      {/* Timeline indicator */}
      <div className="flex flex-col items-center">
        <div
          className={`flex items-center justify-center w-10 h-10 rounded-full ${
            getEventTypeBadgeColor(event.eventType) === "blue"
              ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
              : getEventTypeBadgeColor(event.eventType) === "amber"
              ? "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
              : getEventTypeBadgeColor(event.eventType) === "purple"
              ? "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"
              : getEventTypeBadgeColor(event.eventType) === "green"
              ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
              : "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400"
          }`}
        >
          {getEventTypeIcon(event.eventType)}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={getEventTypeBadgeColor(event.eventType)}>
                {CASE_MANAGEMENT_EVENT_TYPE_LABELS[event.eventType] || event.eventType}
              </Badge>
              {event.contactMethod && (
                <Badge color="zinc">
                  {CONTACT_METHOD_LABELS[event.contactMethod] || event.contactMethod}
                </Badge>
              )}
              {event.followUpNeeded && (
                <Badge color="red">
                  <BellAlertIcon className="w-3 h-3 mr-1" />
                  Follow-up needed
                </Badge>
              )}
            </div>
            <Text className="font-medium text-zinc-900 dark:text-zinc-100">
              {event.summary}
            </Text>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="text-right">
              <Text className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                {formattedDate}
              </Text>
              <Text className="text-xs text-zinc-500 dark:text-zinc-400">
                {formattedTime}
              </Text>
            </div>

            <Dropdown>
              <DropdownButton plain aria-label="More options">
                <EllipsisHorizontalIcon className="w-5 h-5" />
              </DropdownButton>
              <DropdownMenu anchor="bottom end">
                <DropdownItem onClick={() => onEdit(event)}>
                  <PencilIcon className="w-4 h-4" />
                  Edit
                </DropdownItem>
                <DropdownItem onClick={() => onDelete(event)}>
                  <TrashIcon className="w-4 h-4 text-red-500" />
                  <span className="text-red-500">Delete</span>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>

        {/* Notes */}
        {event.notes && (
          <Text className="mt-2 text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2">
            {event.notes}
          </Text>
        )}

        {/* Outcome */}
        {event.outcome && (
          <div className="mt-2">
            <Text className="text-xs font-medium text-zinc-500 dark:text-zinc-400">
              Outcome:
            </Text>
            <Text className="text-sm text-zinc-700 dark:text-zinc-300">
              {event.outcome}
            </Text>
          </div>
        )}

        {/* Follow-up date */}
        {event.followUpNeeded && event.followUpDate && (
          <div className="mt-2">
            <Text className="text-xs text-amber-600 dark:text-amber-400">
              Follow-up by:{" "}
              {(() => {
                try {
                  let followUpDate: Date;
                  if (event.followUpDate?.toDate) {
                    // Firestore Timestamp object
                    followUpDate = event.followUpDate.toDate();
                  } else if (typeof event.followUpDate === 'string') {
                    // ISO string from callable function return
                    followUpDate = new Date(event.followUpDate);
                  } else if ((event.followUpDate as any)?._seconds) {
                    // Firestore Timestamp serialized format
                    followUpDate = new Date((event.followUpDate as any)._seconds * 1000);
                  } else {
                    followUpDate = new Date(event.followUpDate as any);
                  }

                  const formatted = moment(followUpDate);
                  return formatted.isValid() ? formatted.format("MMM D, YYYY") : "Invalid date";
                } catch {
                  return "Invalid date";
                }
              })()}
            </Text>
          </div>
        )}

        {/* Created by */}
        {event.createdByName && (
          <Text className="mt-2 text-xs text-zinc-400 dark:text-zinc-500">
            Added by {event.createdByName}
          </Text>
        )}
      </div>
    </div>
  );
};

export default CaseManagementEventCard;
